/**
 * Protocolo del Bridge (spike del explorador, ideas.md I-18): la app de
 * Node que corre en la red de quien tiene el ERP y deja a la API de Skein
 * leer su base de datos por HTTP. UNA fuente para los dos lados: el Bridge
 * valida lo que recibe con estos esquemas y la API valida lo que le vuelve.
 *
 * Lo que fija este fichero y por qué:
 * - El Bridge es tonto a propósito: cuatro operaciones (salud, objetos,
 *   describir, consulta) y ninguna inteligencia. El agente que decide qué
 *   consultar vive en la API de Skein; el Bridge no lleva modelo ni clave
 *   de modelo. Es lo que un departamento de IT puede auditar.
 * - Dos alcances, cada uno con su propio usuario de base de datos:
 *   `curado` solo ve el esquema de vistas que escribe Skein; `crudo` ve
 *   también las tablas del ERP. La barrera es el permiso del usuario en
 *   la base, nunca un filtro de texto sobre la consulta.
 * - Solo lectura por construcción: los usuarios no tienen más permiso que
 *   SELECT. El Bridge no intenta adivinar si una consulta escribe.
 * - Topes en el Bridge, no en quien llama: filas por consulta, tiempo por
 *   consulta y longitud del texto.
 * - Claves en español como el resto de contratos del Studio; los nombres
 *   de columnas y objetos viajan tal cual vienen de la base.
 */
import { z } from "zod";

export const BRIDGE_PROTOCOLO = "v1";
export const BRIDGE_ALCANCES = ["curado", "crudo"] as const;
/** Filas que devuelve una consulta si quien llama no pide otra cifra. */
export const BRIDGE_FILAS_DEFECTO = 200;
/** Tope duro de filas por consulta: lo que pase de aquí se corta y se marca `truncado`. */
export const BRIDGE_FILAS_MAX = 1000;
/** Tope del texto de una consulta (caracteres). */
export const BRIDGE_SQL_MAX = 20_000;
/** Tope de objetos en un listado: el esquema crudo de BC tiene miles de tablas. */
export const BRIDGE_OBJETOS_MAX = 500;

export const BridgeAlcance = z.enum(BRIDGE_ALCANCES);
export type BridgeAlcance = z.infer<typeof BridgeAlcance>;

/** Nombre de objeto de SQL Server: los de BC llevan espacios, puntos, `$` y guiones bajos. */
const NombreObjeto = z.string().min(1).max(256);
const Esquema = z.string().min(1).max(128);

// ── Salud ─────────────────────────────────────────────────────────────

export const BridgeSalud = z.object({
  ok: z.literal(true),
  protocolo: z.literal(BRIDGE_PROTOCOLO),
  motor: z.literal("sqlserver"),
  baseDeDatos: z.string(),
  version: z.string(),
  alcances: z.array(BridgeAlcance),
});
export type BridgeSalud = z.infer<typeof BridgeSalud>;

// ── Objetos (tablas y vistas que el alcance deja ver) ─────────────────

export const BridgeObjetosPeticion = z.object({
  alcance: BridgeAlcance,
  /** Trozo del nombre, sin comodines: «Ledger Entry», «Purch». */
  buscar: z.string().max(100).optional(),
});
export type BridgeObjetosPeticion = z.infer<typeof BridgeObjetosPeticion>;

export const BridgeObjeto = z.object({
  esquema: Esquema,
  nombre: NombreObjeto,
  tipo: z.enum(["vista", "tabla"]),
  /** La descripción de negocio que Skein escribe en cada vista curada (MS_Description). */
  descripcion: z.string().optional(),
});
export type BridgeObjeto = z.infer<typeof BridgeObjeto>;

export const BridgeObjetosRespuesta = z.object({
  alcance: BridgeAlcance,
  objetos: z.array(BridgeObjeto),
  truncado: z.boolean(),
});
export type BridgeObjetosRespuesta = z.infer<typeof BridgeObjetosRespuesta>;

// ── Describir un objeto ───────────────────────────────────────────────

export const BridgeDescribirPeticion = z.object({
  alcance: BridgeAlcance,
  esquema: Esquema,
  nombre: NombreObjeto,
});
export type BridgeDescribirPeticion = z.infer<typeof BridgeDescribirPeticion>;

export const BridgeColumna = z.object({
  nombre: z.string(),
  tipo: z.string(),
  nulos: z.boolean(),
  descripcion: z.string().optional(),
});
export type BridgeColumna = z.infer<typeof BridgeColumna>;

export const BridgeDescribirRespuesta = z.object({
  esquema: Esquema,
  nombre: NombreObjeto,
  tipo: z.enum(["vista", "tabla"]),
  descripcion: z.string().optional(),
  columnas: z.array(BridgeColumna),
});
export type BridgeDescribirRespuesta = z.infer<typeof BridgeDescribirRespuesta>;

// ── Consulta ──────────────────────────────────────────────────────────

export const BridgeConsultaPeticion = z.object({
  alcance: BridgeAlcance,
  sql: z.string().min(1).max(BRIDGE_SQL_MAX),
  maxFilas: z.number().int().min(1).max(BRIDGE_FILAS_MAX).optional(),
});
export type BridgeConsultaPeticion = z.infer<typeof BridgeConsultaPeticion>;

/** Un valor de celda ya serializado: fechas en ISO, decimales como número, binarios fuera. */
export const BridgeCelda = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export type BridgeCelda = z.infer<typeof BridgeCelda>;

export const BridgeConsultaRespuesta = z.object({
  columnas: z.array(z.object({ nombre: z.string(), tipo: z.string() })),
  filas: z.array(z.array(BridgeCelda)),
  /** Había más filas que el tope y se cortó. */
  truncado: z.boolean(),
  ms: z.number().int().nonnegative(),
});
export type BridgeConsultaRespuesta = z.infer<typeof BridgeConsultaRespuesta>;

// ── Errores ───────────────────────────────────────────────────────────

export const BRIDGE_ERRORES = [
  "no_autorizado", // falta el token o no es el bueno
  "peticion_invalida", // el cuerpo no cumple el esquema
  "no_encontrado", // ruta u objeto que no existe en ese alcance
  "sin_permiso", // la base negó la lectura al usuario de ese alcance
  "sql_error", // la consulta no compila o falla en la base
  "tiempo_agotado", // la consulta pasó del tope de tiempo
  "fuente_caida", // el Bridge no llega a la base
] as const;
export const BridgeErrorCodigo = z.enum(BRIDGE_ERRORES);
export type BridgeErrorCodigo = z.infer<typeof BridgeErrorCodigo>;

export const BridgeError = z.object({
  error: z.object({ codigo: BridgeErrorCodigo, mensaje: z.string() }),
});
export type BridgeError = z.infer<typeof BridgeError>;
